/**
 * Pre-accounting API (M9). All arithmetic that must be authoritative happens in
 * SQL (triggers/views); these functions only read and call RPCs.
 */
import type { AslenClient, Tables, TablesInsert, TablesUpdate, Views } from '../db';
import type { CategoryKind, InvoiceType, TxType } from '../constants';
import { todayIso } from '../format';
import { fetchLiveRate, type PickedRate } from '../fx';
import { D, toDb } from '../money';
import { parseNumber } from '../money';
import { unwrap } from './errors';

export type Contact = Tables<'contacts'>;
export type ContactBalance = Views<'v_contact_balances'>;
export type AgingRow = Views<'v_ar_ap_aging'>;
export type AccountBalance = Views<'v_account_balances'>;
export type LedgerRow = Views<'v_account_ledger'>;
export type OpenItem = Views<'v_open_items'>;
export type OpenPayment = Views<'v_open_payments'>;
export type Invoice = Tables<'invoices'>;
export type InvoiceLine = Tables<'invoice_lines'>;
export type InvoiceAttachment = Tables<'invoice_attachments'>;
export type Category = Tables<'expense_categories'>;
export type PartnerPositionRow = Views<'v_partner_positions'>;

export interface ContactWithBalances extends Contact {
  balances: ContactBalance[];
  aging: AgingRow[];
}

// ── contacts ────────────────────────────────────────────────────────────────
export async function listContacts(client: AslenClient, companyId: string): Promise<ContactWithBalances[]> {
  const [contacts, balances, aging] = await Promise.all([
    client.from('contacts').select('*').eq('company_id', companyId).is('deleted_at', null).order('name'),
    client.from('v_contact_balances').select('*').eq('company_id', companyId),
    client.from('v_ar_ap_aging').select('*').eq('company_id', companyId),
  ]);
  const cs = unwrap(contacts);
  const bs = unwrap(balances);
  const ag = unwrap(aging);
  return cs.map((c) => ({
    ...c,
    balances: bs.filter((b) => b.contact_id === c.id),
    aging: ag.filter((a) => a.contact_id === c.id),
  }));
}

export async function upsertContact(client: AslenClient, row: TablesInsert<'contacts'>) {
  return unwrap(await client.from('contacts').upsert(row).select().single());
}

export async function archiveContact(client: AslenClient, id: string) {
  unwrap(await client.from('contacts').update({ deleted_at: new Date().toISOString() }).eq('id', id).select('id'));
}

export async function listOpenItems(client: AslenClient, companyId: string, contactId?: string) {
  let q = client.from('v_open_items').select('*').eq('company_id', companyId).order('date');
  if (contactId) q = q.eq('contact_id', contactId);
  return unwrap(await q);
}

/**
 * The advances: receipts and payments whose money is not (fully) spoken for yet.
 * W-27 offers them on the invoice so a payment made before the document arrived
 * can still be matched to it.
 */
export async function listOpenPayments(client: AslenClient, companyId: string, contactId?: string) {
  let q = client.from('v_open_payments').select('*').eq('company_id', companyId).order('date');
  if (contactId) q = q.eq('contact_id', contactId);
  return unwrap(await q);
}

/** Every document behind one contact's balance: the statement (W-26 "Ekstre") walks these two lists. */
export interface ContactStatementRows {
  invoices: Pick<Invoice, 'id' | 'no' | 'date' | 'due_date' | 'type' | 'status' | 'currency' | 'total'>[];
  transactions: Pick<Tables<'transactions'>, 'id' | 'date' | 'type' | 'currency' | 'amount' | 'description'>[];
}

export async function listContactStatement(client: AslenClient, companyId: string, contactId: string): Promise<ContactStatementRows> {
  const [invoices, transactions] = await Promise.all([
    client
      .from('invoices')
      .select('id, no, date, due_date, type, status, currency, total')
      .eq('company_id', companyId)
      .eq('contact_id', contactId)
      .is('deleted_at', null)
      .order('date'),
    client
      .from('transactions')
      .select('id, date, type, currency, amount, description')
      .eq('company_id', companyId)
      .eq('contact_id', contactId)
      .is('deleted_at', null)
      .order('date'),
  ]);
  return { invoices: unwrap(invoices), transactions: unwrap(transactions) };
}

export async function updatePartnerShare(client: AslenClient, contactId: string, sharePct: string | null) {
  const v = sharePct === null || sharePct === '' ? null : Number(toDb(parseNumber(sharePct) ?? 0, 4));
  unwrap(await client.from('contacts').update({ share_pct: v }).eq('id', contactId).select('id'));
}

// ── categories ──────────────────────────────────────────────────────────────
export async function listCategories(client: AslenClient, companyId: string, kind?: CategoryKind) {
  let q = client.from('expense_categories').select('*').eq('company_id', companyId).is('deleted_at', null).order('sort').order('name');
  if (kind) q = q.eq('kind', kind);
  return unwrap(await q);
}

export async function upsertCategory(client: AslenClient, row: TablesInsert<'expense_categories'>) {
  return unwrap(await client.from('expense_categories').upsert(row).select().single());
}

/** Soft delete: past transactions keep their category; it just disappears from pickers. */
export async function archiveCategory(client: AslenClient, id: string) {
  unwrap(await client.from('expense_categories').update({ deleted_at: new Date().toISOString() }).eq('id', id).eq('is_system', false).select('id'));
}

// ── accounts & ledger ───────────────────────────────────────────────────────
export async function listAccounts(client: AslenClient, companyId: string) {
  return unwrap(
    await client.from('v_account_balances').select('*').eq('company_id', companyId).order('type').order('sort').order('name'),
  );
}

export async function upsertAccount(client: AslenClient, row: TablesInsert<'cash_accounts'>) {
  return unwrap(await client.from('cash_accounts').upsert(row).select().single());
}

export async function updateAccount(client: AslenClient, id: string, patch: TablesUpdate<'cash_accounts'>) {
  return unwrap(await client.from('cash_accounts').update(patch).eq('id', id).select().single());
}

export async function listLedger(client: AslenClient, accountId: string, limit = 500) {
  const rows = unwrap(
    await client
      .from('v_account_ledger')
      .select('*')
      .eq('account_id', accountId)
      .order('date', { ascending: false })
      .order('created_at', { ascending: false })
      .limit(limit),
  );
  return rows;
}

/** The joins a transaction card needs: names for the account, the category and the contact, plus
 *  whether a payment is matched to an invoice (a matched row's contact is the allocation's anchor). */
const TX_SELECT =
  '*, category:expense_categories(name, kind), contact:contacts(name), account:cash_accounts!transactions_account_id_fkey(name, currency, type), allocations:payment_allocations!payment_allocations_transaction_id_fkey(id)';

export async function listCompanyTransactions(client: AslenClient, companyId: string, from?: string, to?: string) {
  let q = client
    .from('transactions')
    .select(TX_SELECT)
    .eq('company_id', companyId)
    .is('deleted_at', null)
    .order('date', { ascending: false })
    .order('created_at', { ascending: false });
  if (from) q = q.gte('date', from);
  if (to) q = q.lte('date', to);
  return unwrap(await q);
}

/**
 * One transaction with the same joins listCompanyTransactions returns, so the
 * details drawer shows the identical card wherever it is opened from — the
 * ledger (v_account_ledger) carries the ids but none of the names.
 */
export async function getTransaction(client: AslenClient, id: string) {
  return unwrap(await client.from('transactions').select(TX_SELECT).eq('id', id).single());
}

export interface NewTransaction {
  companyId: string;
  accountId: string;
  type: TxType;
  date: string;
  /** decimal string in account currency */
  amount: string;
  fxRate?: string | null;
  fxSource?: string | null;
  contactId?: string | null;
  categoryId?: string | null;
  invoiceId?: string | null;
  transferAccountId?: string | null;
  transferAmount?: string | null;
  description?: string | null;
  attachmentPath?: string | null;
  vatRateId?: string | null;
  vatAmount?: string | null;
  direction?: 'in' | 'out';
  /** only for fx_diff */
  amountBase?: string | null;
  clientUuid?: string | null;
  allocations?: { invoiceId: string; amount: string; accountAmount?: string }[];
}

export async function createTransaction(client: AslenClient, t: NewTransaction): Promise<string> {
  const payload = {
    company_id: t.companyId,
    account_id: t.accountId,
    type: t.type,
    date: t.date,
    amount: t.type === 'fx_diff' ? '0' : toDb(t.amount),
    fx_rate: t.fxRate ? toDb(t.fxRate, 8) : null,
    fx_source: t.fxSource ?? null,
    fx_at: t.fxRate ? new Date().toISOString() : null,
    contact_id: t.contactId || null,
    category_id: t.categoryId || null,
    invoice_id: t.invoiceId || null,
    transfer_account_id: t.transferAccountId || null,
    transfer_amount: t.transferAmount ? toDb(t.transferAmount) : null,
    description: t.description ?? null,
    attachment_path: t.attachmentPath ?? null,
    vat_rate_id: t.vatRateId || null,
    vat_amount: t.vatAmount ? toDb(t.vatAmount) : '0',
    direction: t.direction ?? null,
    amount_base: t.amountBase ? toDb(t.amountBase) : null,
    client_uuid: t.clientUuid ?? null,
  };
  const allocations = (t.allocations ?? [])
    .filter((a) => D(a.amount).greaterThan(0))
    .map((a) => ({ invoice_id: a.invoiceId, amount: toDb(a.amount), account_amount: a.accountAmount ? toDb(a.accountAmount) : null }));
  return unwrap(await client.rpc('create_transaction', { p_tx: payload, p_allocations: allocations })) as string;
}

/** Fields a correction may touch. Amount, account and rate are not among them: the database
 *  refuses to move them once a payment is matched, and the money leg is the allocation's anchor. */
export interface TransactionPatch {
  date?: string;
  categoryId?: string | null;
  contactId?: string | null;
  description?: string | null;
  attachmentPath?: string | null;
}

export async function updateTransaction(client: AslenClient, id: string, patch: TransactionPatch) {
  const row: TablesUpdate<'transactions'> = {};
  if (patch.date !== undefined) row.date = patch.date;
  if (patch.categoryId !== undefined) row.category_id = patch.categoryId || null;
  if (patch.contactId !== undefined) row.contact_id = patch.contactId || null;
  if (patch.description !== undefined) row.description = patch.description || null;
  if (patch.attachmentPath !== undefined) row.attachment_path = patch.attachmentPath;
  return unwrap(await client.from('transactions').update(row).eq('id', id).select('id').single());
}

export async function deleteTransaction(client: AslenClient, id: string) {
  unwrap(await client.from('transactions').update({ deleted_at: new Date().toISOString() }).eq('id', id).select('id'));
}

/**
 * `accountAmount` is what leaves the paying account and only differs from `amount`
 * when the payment and the invoice are in different currencies. Leaving it out means
 * "same currency, one to one" — a caller that means a foreign leg must pass it, because
 * settling a 1.000 $ invoice with 1.000 ₺ off the account would be silently wrong.
 */
export async function allocatePayment(client: AslenClient, companyId: string, transactionId: string, invoiceId: string, amount: string, accountAmount?: string) {
  return unwrap(
    await client
      .from('payment_allocations')
      .insert({ company_id: companyId, transaction_id: transactionId, invoice_id: invoiceId, amount: Number(toDb(amount)), account_amount: Number(toDb(accountAmount ?? amount)) })
      .select()
      .single(),
  );
}

export async function removeAllocation(client: AslenClient, id: string) {
  unwrap(await client.from('payment_allocations').delete().eq('id', id).select('id'));
}

// ── invoices ────────────────────────────────────────────────────────────────
export type InvoiceListRow = Invoice & { contact: { name: string } | null };

export async function listInvoices(client: AslenClient, companyId: string, type: InvoiceType): Promise<InvoiceListRow[]> {
  return unwrap(
    await client
      .from('invoices')
      .select('*, contact:contacts(name)')
      .eq('company_id', companyId)
      .eq('type', type)
      .is('deleted_at', null)
      .order('date', { ascending: false })
      .order('created_at', { ascending: false }),
  ) as unknown as InvoiceListRow[];
}

export type InvoiceDetail = Invoice & {
  contact: Contact | null;
  lines: InvoiceLine[];
  attachments: InvoiceAttachment[];
  allocations: (Tables<'payment_allocations'> & { transaction: Pick<Tables<'transactions'>, 'id' | 'date' | 'amount' | 'currency' | 'account_id' | 'description'> | null })[];
};

export async function getInvoice(client: AslenClient, id: string): Promise<InvoiceDetail> {
  return unwrap(
    await client
      .from('invoices')
      .select(
        '*, contact:contacts(*), lines:invoice_lines(*), attachments:invoice_attachments(*), allocations:payment_allocations(*, transaction:transactions!payment_allocations_transaction_id_fkey(id, date, amount, currency, account_id, description))',
      )
      .eq('id', id)
      .order('sort', { referencedTable: 'invoice_lines' })
      .order('created_at', { referencedTable: 'invoice_attachments' })
      .single(),
  ) as unknown as InvoiceDetail;
}

/** A document filed on an invoice. invoices.pdf_path keeps pointing at the first one (trigger). */
export async function addInvoiceAttachment(
  client: AslenClient,
  companyId: string,
  invoiceId: string,
  path: string,
  fileName: string,
  contentType?: string | null,
) {
  return unwrap(
    await client
      .from('invoice_attachments')
      .insert({ company_id: companyId, invoice_id: invoiceId, path, file_name: fileName, content_type: contentType ?? null })
      .select()
      .single(),
  );
}

/** Drops the row; the file itself stays in the bucket (storage is append-only for us). */
export async function removeInvoiceAttachment(client: AslenClient, id: string) {
  unwrap(await client.from('invoice_attachments').delete().eq('id', id).select('id'));
}

export interface SaveInvoiceInput {
  id?: string;
  companyId: string;
  type: InvoiceType;
  contactId: string;
  no?: string | null;
  date: string;
  dueDate?: string | null;
  currency: string;
  fxRate?: string | null;
  fxSource?: string | null;
  notes?: string | null;
  pdfPath?: string | null;
  isExport?: boolean;
  lines: {
    /** The item the line buys or sells; set, a purchase invoice can post a goods receipt (`fn_receive_invoice`). */
    itemId?: string | null;
    description: string;
    qty: string;
    unit: string;
    unitPrice: string;
    discountPct?: string | null;
    taxRateId?: string | null;
    taxPct?: string | null;
  }[];
}

export async function saveInvoice(client: AslenClient, inv: SaveInvoiceInput, post: boolean): Promise<string> {
  const header = {
    id: inv.id ?? null,
    company_id: inv.companyId,
    type: inv.type,
    contact_id: inv.contactId,
    no: inv.no ?? null,
    date: inv.date,
    due_date: inv.dueDate || null,
    currency: inv.currency,
    fx_rate: inv.fxRate ? toDb(inv.fxRate, 8) : null,
    fx_source: inv.fxSource ?? null,
    notes: inv.notes ?? null,
    pdf_path: inv.pdfPath ?? null,
    is_export: inv.isExport ?? false,
  };
  const lines = inv.lines.map((l) => ({
    item_id: l.itemId || null,
    description: l.description,
    qty: toDb(l.qty, 3),
    unit: l.unit,
    unit_price: toDb(l.unitPrice),
    discount_pct: l.discountPct ? toDb(l.discountPct, 4) : '0',
    tax_rate_id: l.taxRateId || null,
    tax_pct: l.taxPct ? toDb(l.taxPct, 4) : '0',
  }));
  return unwrap(await client.rpc('save_invoice', { p_invoice: header, p_lines: lines, p_post: post })) as string;
}

/**
 * What `fn_receive_invoice` answers with. `skipped` is not an error: a line with
 * no item, or a service line (nakliye, komisyon), is deliberately left out of
 * stock — the user needs to see which ones, not a silent partial receipt.
 */
export interface InvoiceReceipt {
  invoice_id: string;
  invoice_no: string | null;
  contact: string | null;
  batch_id: string;
  location_id: string;
  location_code: string | null;
  occurred_at: string;
  currency: string;
  fx_rate: number | null;
  movements: number;
  value_base: number;
  lines: {
    line_id: string;
    sort: number;
    item_id: string;
    sku: string;
    name: string;
    movement_id: string;
    movement_no: string;
    lot_no: string | null;
    qty: number;
    unit: string;
    qty_base: number;
    entered_unit_cost: number;
    unit_cost_base: number | null;
    value_base: number | null;
  }[];
  skipped: { line_id: string; sort: number; description: string; sku?: string; reason: 'no_item' | 'item_gone' | 'service' }[];
}

/**
 * Posts the stock side of a purchase invoice: every line that carries an item
 * becomes an inbound movement costed at the line's own net-of-VAT unit price and
 * the invoice's frozen FX rate. Owner/partner only (entering a cost is a
 * master-data act), and refused twice for the same invoice.
 */
export async function receiveInvoice(
  client: AslenClient,
  invoiceId: string,
  locationId: string,
  opts: { occurredAt?: string | null; note?: string | null } = {},
): Promise<InvoiceReceipt> {
  return unwrap(
    await client.rpc('fn_receive_invoice', {
      p_invoice_id: invoiceId,
      p_location_id: locationId,
      p_occurred_at: opts.occurredAt ?? undefined,
      p_note: opts.note ?? undefined,
    }),
  ) as unknown as InvoiceReceipt;
}

/** Reverses the whole receipt in one batch — a single movement of it cannot be undone on its own. */
export async function unreceiveInvoice(client: AslenClient, invoiceId: string, note?: string | null) {
  return unwrap(await client.rpc('fn_unreceive_invoice', { p_invoice_id: invoiceId, p_note: note ?? undefined })) as unknown as {
    invoice_id: string;
    batch_id: string;
    movements: number;
  };
}

/** Receipt summary per invoice; `movements === 0` means the receipt was fully reversed. */
export async function invoiceReceiptStatus(client: AslenClient, invoiceId: string) {
  const rows = unwrap(await client.from('v_invoice_receipts').select('*').eq('invoice_id', invoiceId));
  return rows[0] ?? null;
}

export async function cancelInvoice(client: AslenClient, id: string) {
  unwrap(await client.from('invoices').update({ status: 'cancelled' }).eq('id', id).select('id'));
}

export async function deleteDraftInvoice(client: AslenClient, id: string) {
  unwrap(await client.from('invoices').update({ deleted_at: new Date().toISOString() }).eq('id', id).eq('status', 'draft').select('id'));
}

// ── global search (⌘K) ──────────────────────────────────────────────────────
export interface AccountingSearchResult {
  contacts: Contact[];
  invoices: InvoiceListRow[];
  transactions: Pick<Tables<'transactions'>, 'id' | 'date' | 'type' | 'amount' | 'currency' | 'description' | 'account_id'>[];
}

/** PostgREST reads `,` `(` `)` as filter syntax and `*` as the wildcard, so a typed term is stripped of them. */
function likeTerm(term: string): string {
  return term.trim().replace(/[,()*\\]/g, ' ').trim();
}

/**
 * What the top bar promises: contacts by name or tax number, invoices by
 * document number, transactions by description. One query per entity, filtered
 * and cut short by the server — the palette never pulls a whole table down.
 */
export async function searchAccounting(client: AslenClient, companyId: string, term: string, limit = 6): Promise<AccountingSearchResult> {
  const q = likeTerm(term);
  if (!q) return { contacts: [], invoices: [], transactions: [] };
  const [contacts, invoices, transactions] = await Promise.all([
    client
      .from('contacts')
      .select('*')
      .eq('company_id', companyId)
      .is('deleted_at', null)
      .or(`name.ilike.*${q}*,tax_no.ilike.*${q}*`)
      .order('name')
      .limit(limit),
    client
      .from('invoices')
      .select('*, contact:contacts(name)')
      .eq('company_id', companyId)
      .is('deleted_at', null)
      .ilike('no', `*${q}*`)
      .order('date', { ascending: false })
      .limit(limit),
    client
      .from('transactions')
      .select('id, date, type, amount, currency, description, account_id')
      .eq('company_id', companyId)
      .is('deleted_at', null)
      .ilike('description', `*${q}*`)
      .order('date', { ascending: false })
      .limit(limit),
  ]);
  return {
    contacts: unwrap(contacts),
    invoices: unwrap(invoices) as unknown as InvoiceListRow[],
    transactions: unwrap(transactions),
  };
}

// ── partners, VAT, period ───────────────────────────────────────────────────
/** The partner's account in `currency` (opened on first use). contactId defaults to the caller. */
export async function ensurePartnerAccount(client: AslenClient, companyId: string, currency: string, contactId?: string | null): Promise<string> {
  return unwrap(await client.rpc('ensure_partner_account', { p_company: companyId, p_currency: currency, p_contact: contactId ?? undefined })) as string;
}

export async function listPartnerPositions(client: AslenClient, companyId: string) {
  return unwrap(await client.from('v_partner_positions').select('*').eq('company_id', companyId).order('name'));
}

export async function vatSummary(client: AslenClient, companyId: string, from: string, to: string) {
  return unwrap(
    await client.from('v_vat_summary').select('*').eq('company_id', companyId).gte('period', from).lte('period', to).order('tax_pct', { ascending: false }),
  );
}

export async function categoryTotals(client: AslenClient, companyId: string, from: string, to: string) {
  return unwrap(await client.from('v_category_totals').select('*').eq('company_id', companyId).gte('period', from).lte('period', to));
}

// ── the cost of the partners' money ─────────────────────────────────────────
// A management figure, never a legal expense: see migration 20261002000001. The
// arithmetic lives in SQL so the web and the phone cannot drift apart on it.
export type CapitalCostRate = Tables<'capital_cost_rates'>;
export type CapitalCostRow = Awaited<ReturnType<typeof capitalCost>>[number];

export async function listCapitalCostRates(client: AslenClient, companyId: string): Promise<CapitalCostRate[]> {
  return unwrap(
    await client
      .from('capital_cost_rates')
      .select('*')
      .eq('company_id', companyId)
      .is('deleted_at', null)
      .order('currency')
      .order('effective_from', { ascending: false }),
  );
}

/**
 * With `id` this updates that rate (the primary key is the conflict arbiter PostgREST
 * infers); without one it inserts. A second rate for the same currency and date hits the
 * partial unique index and comes back as 'Bu kayıt zaten var' — which is why the settings
 * table opens an existing row for editing instead of asking for it again.
 */
export async function upsertCapitalCostRate(
  client: AslenClient,
  row: Omit<TablesInsert<'capital_cost_rates'>, 'id'> & { id?: string },
): Promise<CapitalCostRate> {
  const [saved] = unwrap(await client.from('capital_cost_rates').upsert(row).select('*'));
  return saved!;
}

/** Rates are soft-deleted: a figure somebody read last month must stay explainable. */
export async function deleteCapitalCostRate(client: AslenClient, id: string): Promise<void> {
  unwrap(await client.from('capital_cost_rates').update({ deleted_at: new Date().toISOString() }).eq('id', id).select('id'));
}

/**
 * The company is an argument, not an afterthought: without it the function answered for
 * every company the caller is a member of, and a two-company owner read one company's
 * card with the other's partners folded in.
 */
export async function capitalCost(client: AslenClient, companyId: string, from: string, to: string) {
  return unwrap(await client.rpc('capital_cost', { p_company: companyId, p_from: from, p_to: to }));
}

// ── FX ──────────────────────────────────────────────────────────────────────
export async function lookupFxRate(client: AslenClient, from: string, to: string, date: string, companyId?: string) {
  const rows = unwrap(await client.rpc('get_fx_rate', { p_from: from, p_to: to, p_date: date, p_company: companyId }));
  return (rows as { rate: number; source: string; rate_date: string }[])[0] ?? null;
}

/**
 * The rate an entry must carry, resolved the way the web form does it: the stored rate for the date
 * first, then the live one (which is filed so the next entry finds it stored). Returns null only
 * when there is no rate anywhere — the caller decides what to tell the user, because sending the
 * entry without a rate would fail in the database with a raw error instead. Transport failures are
 * not swallowed: a lookup that cannot reach the server throws, so the entry stays retryable.
 *
 * The live rate is only ever accepted for *today*, which is the same gate the web form keeps
 * (`useEntryRate`: `autoFetch && date === todayIso()`). Frankfurter's endpoint is `/v1/latest` and
 * takes no date, so for a backdated entry it answers with today's figure — booking a 1 Eylül
 * expense at 26 Ekim's rate, silently, under `fx_source = 'FRANKFURTER'`, with the frozen
 * `amount_base` wrong for good. When no rate is on file for that date there is nothing honest left
 * to return, so this says so and the caller asks for the rate to be entered.
 */
export async function resolveEntryRate(
  client: AslenClient,
  companyId: string,
  from: string,
  to: string,
  date: string,
): Promise<{ rate: string; source: string } | null> {
  if (from === to) return { rate: '1', source: 'IDENTITY' };
  const stored = await lookupFxRate(client, from, to, date, companyId);
  if (stored) return { rate: String(stored.rate), source: stored.source };
  if (date !== todayIso()) return null;
  let live: PickedRate;
  try {
    live = await fetchLiveRate(from, to);
  } catch {
    return null; // rate service unreachable or silent about this pair
  }
  // best effort: the entry is saved with the rate even when filing it is refused (e.g. operator role)
  await captureRate(client, companyId, from, to, live.date, live.rate.toString(), 'FRANKFURTER').catch(() => false);
  return { rate: live.rate.toString(), source: 'FRANKFURTER' };
}

/** Stores a live rate fetched at entry time so the document's rate is traceable. */
export async function captureRate(client: AslenClient, companyId: string, from: string, to: string, date: string, rate: string, source: 'FRANKFURTER' | 'MANUAL') {
  const { error } = await client
    .from('fx_rates')
    .insert({ company_id: companyId, date, from_currency: from, to_currency: to, rate: Number(toDb(rate, 8)), source });
  // a rate for the same day/source may already exist — that is fine
  return !error || (error as { code?: string }).code === '23505';
}
